'use client';

import React, { useMemo, useRef, useState } from 'react';
import { Flex } from '@chakra-ui/react';
import { PRODUCT_CATEGORY_LABELS, type ProductCategory } from '@/constants/product/category';
import { scrollHorizontally, DEFAULT_SCROLL_DURATION } from '../utils/scrollUtils';
import { useEventListener } from '@/hooks/utils/useEventListener';
import ScrollButton from './ScrollButton';
import CategoryButton from './CategoryButton';

interface TopRankedCategoryFilterProps {
  selectedCategory: ProductCategory;
  onCategoryChange: (category: ProductCategory) => void;
}

const TopRankedCategoryFilter = ({ selectedCategory, onCategoryChange }: TopRankedCategoryFilterProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(true);
  const [isScrolling, setIsScrolling] = useState(false);

  const categories = useMemo(() => {
    return Object.entries(PRODUCT_CATEGORY_LABELS).map(([id, name]) => ({
      id: id as ProductCategory,
      name,
    }));
  }, []);

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftButton(scrollLeft > 0);
      setShowRightButton(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const handleScroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current && !isScrolling) {
      setIsScrolling(true);

      scrollHorizontally(scrollContainerRef.current, direction, DEFAULT_SCROLL_DURATION, () => {
        setIsScrolling(false);
        checkScrollButtons();
      });
    }
  };

  useEventListener('scroll', checkScrollButtons, scrollContainerRef.current);

  return (
    <Flex position="relative" width="100%">
      <ScrollButton
        direction="left"
        isVisible={showLeftButton}
        onClick={() => handleScroll('left')}
        isScrolling={isScrolling}
      />

      <Flex
        ref={scrollContainerRef}
        width="100%"
        gap={2}
        overflowX="auto"
        css={{
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
        }}
      >
        {categories.map((category) => (
          <CategoryButton
            key={category.id}
            name={category.name}
            isSelected={selectedCategory === category.id}
            onClick={() => onCategoryChange(category.id)}
          />
        ))}
      </Flex>

      <ScrollButton
        direction="right"
        isVisible={showRightButton}
        onClick={() => handleScroll('right')}
        isScrolling={isScrolling}
      />
    </Flex>
  );
};

export default TopRankedCategoryFilter;
